import dayjs from 'dayjs'
import merge from 'lodash/merge'
import { Channel, ChannelState } from 'phoenix'
import { defineStore } from 'pinia'

import { SocketChannels } from '@shared/models/enums/websocket-channels'
import {
  ConversationChannelEvents,
  ConversationNotificationEvents,
  UserChannelEvents,
} from '@shared/models/enums/websocket-events'
import { ChatNotificatioPayload } from '@shared/models/one-of-okind/chat-notification'
import {
  AssigneeUpdate,
  ChatNotification,
  ConversationParticipantsUpdate,
  NewMessage,
  NotifyChime,
  ReassignConversation,
  StatusUpdate,
} from '@shared/models/proto/base/chat'
import { Nullable, Optional } from '@shared/models/types/primary'
import { IsTypingEvent } from '@shared/models/types/websocket'
import { SessionProvider } from '@shared/plugins/session-provider'
import { PhoenixSocket } from '@shared/plugins/socket'
import { useGeneralStore } from '@shared/store/general'
import { useTypingIndicatorsStore } from '@shared/store/typing-indicators'
import {
  BOT_TYPING_TIMEOUT,
  TYPING_TIMEOUT,
} from '@shared/utils/constants/time'
import {
  updateMessagesDeliveredStatus,
  updateMessagesReadStatus,
  updateNonQlickMessagesReadStatus,
} from '@shared/utils/helpers/chat/messages'
import { isBotConversation } from '@shared/utils/helpers/conversation/is-bot-conversation'
import { isGptBotPayload } from '@shared/utils/helpers/messages/bot-payload'
import { playSound, SoundTypes } from '@shared/utils/helpers/sounds'
import { updateState } from '@shared/utils/store/http-request-state'

import { NewNonqliqMessage } from '@protos/ampq/chat/new_nonqliq_message_pb'
import { RefreshedMessage } from '@protos/ampq/chat/refreshed_message_pb'
import {
  ParticipantEvent,
  ParticipantEventType,
} from '@protos/ampq/conversation/participant_event_pb'
import { ConversationNotification } from '@protos/ampq/conversation_notification_pb'
import { Conversation as PatientConversation } from '@protos/apps/pc/conversation_pb'
import { Conversation } from '@protos/chat/conversation_pb'
import { Message } from '@protos/chat/message_pb'
import { MessageType } from '@protos/chat/message_type_pb'
import { NonqliqMessage } from '@protos/chat/nonqliq_message_pb'
import { ConversationStatus } from '@protos/conversation_enums_pb'

import {
  ICreateMessageData,
  ICreateMessageFile,
} from '@pc/models/proto/base/messages'
import { MessageCreateDTO } from '@pc/models/proto/dto/messages'
import { MessagesIndexRO } from '@pc/models/proto/ro/messages'
import { NonqliqMessagesIndexRO } from '@pc/models/proto/ro/non-qliq-messages'
import { useConversationsStore } from '@pc/store/conversations'
import { useMessageAttachmentsStore } from '@pc/store/message-attachments'
import { useNonqliqMessagesStore } from '@pc/store/non-qliq-messages'
import { useSessionStore } from '@pc/store/session'

import { useMessagesStore } from './messages'

interface State {
  chatUserChannelState: Nullable<ChannelState>
  conversationChannelState: Nullable<ChannelState>
  isFirstLoading: boolean
  isSocketLoading: boolean
}

const state = (): State => ({
  chatUserChannelState: null,
  conversationChannelState: null,
  isFirstLoading: true,
  isSocketLoading: false,
})

export const useSocketStore = defineStore('socket', {
  state,

  actions: {
    async createSocketConnection() {
      const peerProps = SessionProvider.apiEssentials().wsParams
      if (peerProps) {
        // isSocketLoading set 'false' only until  connect to conversation cannel
        this.isSocketLoading = true

        const { wsToken, wsUrl } = peerProps
        await PhoenixSocket.connect(wsToken, wsUrl)
        await this.subscribeChatUser()
      }
    },

    closeSocketConnection() {
      PhoenixSocket.disconnect()
      this.unSubscribeChatUser()
    },

    onChangeChatUserChannelState(channelState: ChannelState) {
      this.chatUserChannelState = channelState
    },

    onChangeConversationChannelState(channelState: ChannelState, id: number) {
      this.isSocketLoading = false
      if (this.isCurrentConversation(id)) {
        this.conversationChannelState = channelState
      }
    },

    // ChatUserChannel
    isChatUserSubscribed() {
      return PhoenixSocket.isChannelSubscribed(
        SessionProvider.chatUserId(),
        SocketChannels.ChatUserChannel
      )
    },

    async subscribeChatUser() {
      const sessionStore = useSessionStore()
      if (sessionStore.chatProps?.visitant.case === 'videoCallGuest') {
        return
      }

      const { channel } = await PhoenixSocket.findChannel(
        SessionProvider.chatUserId(),
        SocketChannels.ChatUserChannel,
        this.onChangeChatUserChannelState
      )
      this.subsribeToChatNotificationEvents(channel)
    },

    subsribeToChatNotificationEvents(channel: Channel) {
      channel.on(UserChannelEvents.NewConversation, (json) => {
        this.handleChatNotification(ChatNotification.fromJson(json))
      })

      channel.on(UserChannelEvents.RefreshedMessage, (json) => {
        this.handleChatNotification(ChatNotification.fromJson(json))
      })

      channel.on(UserChannelEvents.ReassignConversation, (json) => {
        this.handleChatNotification(ChatNotification.fromJson(json))
      })

      channel.on(UserChannelEvents.AssigneeUpdate, (json) => {
        this.handleChatNotification(ChatNotification.fromJson(json))
      })

      channel.on(UserChannelEvents.StatusUpdate, (json) => {
        this.handleChatNotification(ChatNotification.fromJson(json))
      })

      channel.on(UserChannelEvents.NewMessage, (json) => {
        this.handleChatNotification(ChatNotification.fromJson(json))
      })

      channel.on(UserChannelEvents.NotifyChime, (json) => {
        this.handleChatNotification(ChatNotification.fromJson(json))
      })

      channel.on(UserChannelEvents.ConversationParticipantsUpdate, (json) => {
        this.handleChatNotification(ChatNotification.fromJson(json))
      })

      channel.on(UserChannelEvents.NewNonqliqMessage, (json) => {
        this.handleChatNotification(ChatNotification.fromJson(json))
      })
    },

    unSubscribeChatUser() {
      PhoenixSocket.leaveChannel(
        SessionProvider.chatUserId(),
        SocketChannels.ChatUserChannel
      )
    },

    handleChatNotification(event: ChatNotification) {
      switch (event.payload.case) {
        case ChatNotificatioPayload.newMessage:
          this.handleNewMessageNotification(event.payload.value)
          break
        case ChatNotificatioPayload.reassignConversation:
          this.handleReassignConversationNotification(
            event.payload.value,
            event.chime
          )
          break
        case ChatNotificatioPayload.assigneeUpdate:
          this.handleAssigneeUpdateNotification(event.payload.value)
          break
        case ChatNotificatioPayload.statusUpdate:
          this.handleStatusUpdateNotification(event.payload.value)
          break
        case ChatNotificatioPayload.notifyChime:
          this.handleNotifyChimeNotification(event.payload.value, event.chime)
          break
        case ChatNotificatioPayload.refreshedMessage:
          this.handleRefreshedMessage(event.payload.value)
          break
        case ChatNotificatioPayload.conversationParticipantsUpdate:
          this.handleConversationParticipantsUpdateNotification(
            event.payload.value
          )
          break
        case ChatNotificatioPayload.newNonqliqMessage:
          this.handleNewNonqliqMessage(event.payload.value, event.chime)
      }
    },

    isCurrentConversation(conversationId: number) {
      const conversationsStore = useConversationsStore()
      return conversationsStore.currentConversationId === conversationId
    },

    handleRefreshedMessage(payload: RefreshedMessage) {
      const message = payload.data
      if (!message) {
        return
      }

      const messagesStore = useMessagesStore()

      const time = dayjs().toISOString()

      const messageToAdd = merge(message, {
        actualStatus: {
          deliveredTime: time,
        },
      })

      messagesStore.updateAiMessage(messageToAdd)
    },

    handleNewNonqliqMessage(payload: NewNonqliqMessage, chime: boolean) {
      const message = payload.data
      if (!message) {
        return
      }
      const generalStore = useGeneralStore()
      const nonQliqMessagesStore = useNonqliqMessagesStore()

      const isMyMessage = `${message.senderId}` === generalStore.currentUser?.id
      const time = new Date().toISOString()

      const messageToAdd = isMyMessage
        ? message
        : merge(message, {
            deliveredTime: time,
          } as unknown as NonqliqMessage)

      nonQliqMessagesStore.addMessageToList(messageToAdd)

      if (chime && !isMyMessage) {
        playSound(SoundTypes.General)
      }
    },

    async handleNewMessageNotification(payload: NewMessage) {
      const { data: message } = payload
      if (!message || !this.isCurrentConversation(message.conversationId)) {
        /* eslint-disable no-console */
        console.log('is not CurrentConversationMessage:>> ', payload)
        return
      }

      const isStatusUpdate = [
        MessageType.ASSIGNEE_UPDATE_NOTIFICATION,
        MessageType.STATUS_UPDATE_NOTIFICATION,
      ].includes(message.messageType)

      const typingIndicatorsStore = useTypingIndicatorsStore()
      typingIndicatorsStore.setTypingStatus({
        conversationId: message.conversationId,
        isTyping: false,
      })
      const messagesStore = useMessagesStore()
      const generalStore = useGeneralStore()
      const isMyMessage =
        `${message.sender?.id}` === generalStore.currentUser?.id

      if (this.shouldProcessEvent(message.chatWidgetId)) {
        const time = new Date().toISOString()

        if (!isMyMessage) {
          this.pushMarkDelivered(message.conversationId, message.id, time)
        }

        if (isStatusUpdate) {
          await this.fetchConversationById(message.conversationId, {
            force: true,
          })

          const isGptCase = isGptBotPayload(message)

          if (isGptCase) {
            messagesStore.isShowStarters = true
          }
        }

        const messageToAdd = isMyMessage
          ? message
          : merge(message, {
              actualStatus: {
                deliveredTime: time,
              },
            } as Message)

        const animate = isGptBotPayload(message)

        messagesStore.addMessageToConversation(messageToAdd, animate)
        messagesStore.handleSpecialMessages(messageToAdd)
      }

      if (this.isCurrentConversation(message.conversationId) && !isMyMessage) {
        playSound(SoundTypes.General)
      }
    },

    handleReassignConversationNotification(
      payload: ReassignConversation,
      chime: boolean
    ) {
      const conversation = payload.data
      if (!conversation) {
        return
      }
      if (this.shouldProcessEvent(conversation.chatWidgetId)) {
        this.handleReassignConversation(conversation)
      }

      if (
        chime &&
        conversation.status === ConversationStatus.OPEN &&
        this.isCurrentConversation(conversation.id)
      ) {
        playSound(SoundTypes.General)
      }
    },

    handleAssigneeUpdateNotification(payload: AssigneeUpdate) {
      if (this.shouldProcessEvent(payload.chatWidgetId)) {
        this.handleAssigneeUpdate(payload)
      }
    },

    async handleStatusUpdateNotification(payload: StatusUpdate) {
      if (this.isCurrentConversation(payload.conversationId)) {
        await this.fetchConversationById(payload.conversationId)
      }
    },

    handleNotifyChimeNotification(payload: NotifyChime, chime: boolean) {
      if (
        chime &&
        payload.data &&
        this.isCurrentConversation(payload.data?.id)
      ) {
        playSound(
          payload.data?.status === ConversationStatus.ASSIGNED_TO_BOT
            ? SoundTypes.Ping
            : SoundTypes.General
        )
      }
    },

    handleConversationParticipantsUpdateNotification(
      payload: ConversationParticipantsUpdate
    ) {
      const conversation = payload.data

      if (!conversation) {
        return
      }

      this.handleConversationParticipantsUpdate(conversation)
    },

    shouldProcessEvent(chatWidgetId?: Nullable<number>) {
      if (!chatWidgetId) {
        return true
      }

      const sessionStore = useSessionStore()

      const isAllConversationsSelected = !sessionStore.chatProps?.chatWidget
      const isCurrentWidgetSelected =
        sessionStore.chatProps?.chatWidget?.id === chatWidgetId

      return isAllConversationsSelected || isCurrentWidgetSelected
    },

    handleReassignConversation({ id: conversationId }: Conversation) {
      this.fetchConversationById(conversationId)
    },

    handleAssigneeUpdate(assigneeUpdateMessage: AssigneeUpdate) {
      this.fetchConversationById(assigneeUpdateMessage.conversationId)
    },

    handleConversationParticipantsUpdate({ id: conversationId }: Conversation) {
      this.fetchConversationById(conversationId)
    },

    async findConversationChannel(conversationId?: number) {
      if (!conversationId) {
        return null
      }
      try {
        const { channel } = await PhoenixSocket.findChannel(
          conversationId,
          SocketChannels.ConversationChannel,
          this.onChangeConversationChannelState
        )
        return channel
      } catch (error) {
        /* eslint-disable no-console */
        console.error('[patient-chat] Error findConversationChannel', error)
        return null
      }
    },

    //createConversationChannel with unsubscribing previous Conversations and with fetching data
    async subscribeConversationWithFetch(conversationId: number) {
      const typingIndicatorsStore = useTypingIndicatorsStore()
      typingIndicatorsStore.resetTypingStatus(conversationId)

      await this.getOrFetchConversationById(conversationId, { force: true })

      PhoenixSocket.leavePreviousChannels(
        conversationId,
        SocketChannels.ConversationChannel
      )
      const channel = await this.findConversationChannel(conversationId)

      if (channel) {
        this.subsribeToConversationEvents(channel)
      }
    },

    subsribeToConversationEvents(channel: Channel) {
      channel.on(ConversationChannelEvents.IsTyping, this.handleTyping)
      channel.on(
        ConversationChannelEvents.FileUploadFinished,
        this.handleFileUploadFinished
      )
      channel.on(
        ConversationChannelEvents.MarkDelivered,
        this.onMessageStatusChange
      )
      channel.on(ConversationChannelEvents.MarkRead, this.onReadStatusChange)

      channel.on(ConversationNotificationEvents.ParticipantEvent, (json) => {
        this.handleConversationNotification(
          ConversationNotification.fromJson(json)
        )
      })
    },

    handleConversationNotification(notification: ConversationNotification) {
      switch (notification.event.case) {
        case 'participantEvent':
          this.handleConversationParticipantEvent(notification.event.value)
          break
      }
    },

    unsubscribeConversation(conversationId: number) {
      PhoenixSocket.leaveChannel(
        conversationId,
        SocketChannels.ConversationChannel
      )
    },

    handleConversationParticipantEvent({
      chatUserId,
      eventType,
    }: ParticipantEvent) {
      const conversationsStore = useConversationsStore()
      const isOnline =
        eventType === ParticipantEventType.PARTICIPANT_WENT_ONLINE
      conversationsStore.updateParticipantByChatUserId(chatUserId, { isOnline })
    },

    async pushIsTyping(conversationId: number, isTyping: boolean) {
      const channel = await this.findConversationChannel(conversationId)
      if (channel) {
        channel.push(ConversationChannelEvents.IsTyping, { typing: isTyping })
      }
    },

    async pushMarkRead(
      conversationId: number,
      messageId: number,
      time: string
    ) {
      const channel = await this.findConversationChannel(conversationId)
      if (channel) {
        channel.push(ConversationChannelEvents.MarkRead, {
          message_id: messageId,
          time,
        })
      }
    },

    async pushMarkDelivered(
      conversationId: number,
      messageId: number,
      time: string
    ) {
      const channel = await this.findConversationChannel(conversationId)
      if (channel) {
        await channel.push(ConversationChannelEvents.MarkDelivered, {
          message_id: messageId,
          time,
        })
      }
    },

    async pushFileUploadFinished(conversationId: number, messageId: number) {
      const channel = await this.findConversationChannel(conversationId)
      if (channel) {
        await channel.push(ConversationChannelEvents.FileUploadFinished, {
          messageId,
        })
      }
    },

    async pushVideoCallCancel(conversationId: number) {
      const channel = await this.findConversationChannel(conversationId)

      if (channel) {
        await channel.push(ConversationChannelEvents.VideoChatCancel, {})
      }
    },

    async pushStopQuestioningEvent(conversationId: number) {
      const channel = await this.findConversationChannel(conversationId)

      if (channel) {
        const conversationStore = useConversationsStore()

        await channel.push(ConversationChannelEvents.StopQuestioning, {})

        await conversationStore.logEndAiAgentConversation(conversationId)
      }
    },
    async refreshAiMessage(params: {
      messageId: number
      conversationId: number
    }) {
      const { messageId, conversationId } = params

      if (messageId) {
        const channel = await this.findConversationChannel(conversationId)

        const messagesStore = useMessagesStore()

        const message = messagesStore.messages.data?.data.find(
          (m) => m.id === messageId
        )

        if (message) {
          message.text = ''
        }

        messagesStore.isRefreshing = true

        if (channel) {
          await channel.push(ConversationChannelEvents.RefreshAiMessage, {
            message_id: messageId,
          })
        }
      }
    },

    async sendChatMessage(dto: {
      data: ICreateMessageData
      files: ICreateMessageFile[]
    }): Promise<Message> {
      const channel = await this.findConversationChannel(
        dto.data?.conversationId
      )
      return new Promise((resolve, reject) => {
        if (channel) {
          channel
            .push(
              ConversationChannelEvents.CreateMessagePc,
              new MessageCreateDTO(dto)
            )
            .receive('ok', resolve)
            .receive('error', reject)
            .receive('timeout', resolve)
        } else {
          reject(
            `failed to find ConversationChannel:${dto.data?.conversationId} in sendChatMessage`
          )
        }
      })
    },

    // TODO: add protos when available
    // eslint-disable-next-line
    onReadStatusChange(data: any) {
      const messagesStore = useMessagesStore()
      const { meta, data: list = [] } = messagesStore.messages.data ?? {}

      messagesStore.messages = updateState(
        messagesStore.messages,
        new MessagesIndexRO({
          meta,
          data: updateMessagesReadStatus(data, list),
        })
      )
    },

    // TODO: add protos when available
    // eslint-disable-next-line
    onMessageStatusChange(data: any) {
      const messagesStore = useMessagesStore()
      const { meta, data: list = [] } = messagesStore.messages.data ?? {}

      messagesStore.messages = updateState(
        messagesStore.messages,
        new MessagesIndexRO({
          meta,
          data: updateMessagesDeliveredStatus(data, list),
        })
      )
    },

    // TODO: add protos when available
    handleTyping({ data }: { data: IsTypingEvent }) {
      const typingIndicatorsStore = useTypingIndicatorsStore()

      if (data.user === SessionProvider.chatUserId() || !data.conversation) {
        return
      }
      const conversationsStore = useConversationsStore()

      const conversation =
        conversationsStore?.currentConversation?.id === data.conversation
          ? conversationsStore.currentConversation
          : null

      const typingTimeout =
        conversation &&
        isBotConversation(conversation as Conversation, data.user)
          ? BOT_TYPING_TIMEOUT
          : TYPING_TIMEOUT

      typingIndicatorsStore.setTypingStatus({
        conversationId: data.conversation,
        isTyping: data.typing,
        timeout: typingTimeout,
      })
    },

    handleFileUploadFinished({ messageId }: { messageId: number }) {
      const messageAttachmentsStore = useMessageAttachmentsStore()
      messageAttachmentsStore.fetchAttachment(messageId)
    },

    async fetchConversationById(
      conversationId: number,
      options?: { force: boolean }
    ): Promise<Optional<PatientConversation>> {
      const conversationsStore = useConversationsStore()
      if (options?.force || !conversationsStore.isPending) {
        return await conversationsStore.fetchConversationById({
          conversationId,
        })
      }
    },

    async getOrFetchConversationById(
      conversationId: number,
      options?: { force: boolean }
    ): Promise<Optional<PatientConversation>> {
      const conversationsStore = useConversationsStore()
      if (
        conversationsStore.getConversationById(conversationId) &&
        this.isFirstLoading
      ) {
        this.isFirstLoading = false
        return conversationsStore.getConversationById(
          conversationId
        ) as Conversation
      } else {
        return await this.fetchConversationById(conversationId, options)
      }
    },

    async subscribeNonqliqConversationChannel(conversationUuid: string) {
      PhoenixSocket.leavePreviousChannels(
        conversationUuid,
        SocketChannels.NonqliqConversationChannel
      )
      await PhoenixSocket.findChannel(
        conversationUuid,
        SocketChannels.NonqliqConversationChannel
      ).then(({ channel }) => {
        channel.on(
          ConversationChannelEvents.MarkRead,
          this.onReadNonQlickStatusChange
        )
      })
    },

    unsubscribeNonqliqConversationChannel(conversationId: string) {
      PhoenixSocket.leaveChannel(
        conversationId,
        SocketChannels.NonqliqConversationChannel
      )
    },

    async markNonQliqMessageRead(call_id: string, conversationUuid: string) {
      const time = new Date().toISOString()
      await PhoenixSocket.findChannel(
        conversationUuid,
        SocketChannels.NonqliqConversationChannel
      ).then(({ channel }) => {
        channel.push(ConversationChannelEvents.MarkRead, {
          call_id,
          time,
        })
      })
    },

    onReadNonQlickStatusChange(data: NonqliqMessage) {
      const nonQliqMessagesStore = useNonqliqMessagesStore()
      const { meta, data: list = [] } = nonQliqMessagesStore.messages.data ?? {}

      nonQliqMessagesStore.messages = updateState(
        nonQliqMessagesStore.messages,
        new NonqliqMessagesIndexRO({
          meta,
          data: updateNonQlickMessagesReadStatus(data, list),
        })
      )
    },
  },

  getters: {
    isChannelsConnected: (state) => {
      return (
        state.chatUserChannelState === 'joined' &&
        state.conversationChannelState === 'joined'
      )
    },
  },
})
